import React, { useEffect, useMemo, useRef, useState } from "react";
import { Crosshair, ExternalLink, Sparkles, Link2, Table2, CornerDownRight, Info, Pin, PinOff, X, Layers3, Plus } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "../ui/command";
import { buildFeatureNames, computeLookup } from "../../lib/lookup";
import { formatPrice } from "../../lib/matrix";
import PathCard from "./PathCard";
import ComparePanel from "./ComparePanel";
import ShareCard from "./ShareCard";
import ShareMenu from "./ShareMenu";

const SECTION_SHORT = { office: "Office 365", ems: "EMS", windows: "Windows", suite: "Suite", related: "Add-ons" };
export const MAX_PINS = 3;

export default function LookupSheet({
  open,
  onOpenChange,
  dataset,
  plans,
  addons,
  groups,
  priceMode,
  pricing,
  feature,
  onFeatureChange,
  onShowInMatrix,
  pinned,
  onPinnedChange,
  mode,
  onModeChange,
}) {
  const [query, setQuery] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const [cardData, setCardData] = useState(null);
  const cardRef = useRef(null);
  const names = useMemo(() => buildFeatureNames(dataset?.rows || []), [dataset]);
  const symbol = pricing?.symbol || "$";

  const result = useMemo(
    () => (feature && dataset ? computeLookup({ rows: dataset.rows, plans, addons, name: feature, priceMode }) : null),
    [feature, dataset, plans, addons, priceMode]
  );

  useEffect(() => {
    if (open) setQuery("");
  }, [open, feature]);

  useEffect(() => {
    if (mode !== "single" || !result) return;
    setCardData({
      title: result.name,
      subtitle: `Every license and add-on path that grants it, cheapest first`,
      tags: result.contexts.map((c) => c.section),
      items: result.paths.slice(0, 5).map((p) => ({ key: p.key, parts: p.parts, total: p.total, estimated: p.estimated, badge: p.type })),
      note: "",
    });
  }, [result, mode]);

  const groupLabel = (id) => groups.find((g) => g.id === id)?.tableLabel || "";
  const filteredPaths = (result?.paths || []).filter(
    (p) => groupFilter === "all" || p.groupId === groupFilter || (groupFilter === "standalone" && p.type === "standalone")
  );
  const usedGroups = groups.filter((g) => (result?.paths || []).some((p) => p.groupId === g.id));
  const hasStandalone = (result?.paths || []).some((p) => p.type === "standalone");
  const showList = mode === "single" && (!feature || query.trim().length > 0);
  const isPinned = !!feature && pinned.some((n) => n.toLowerCase() === feature.toLowerCase());

  const selectFeature = (name) => {
    onFeatureChange(name);
    onModeChange("single");
    setQuery("");
    setGroupFilter("all");
  };

  const togglePin = () => {
    if (!result) return;
    if (isPinned) {
      onPinnedChange(pinned.filter((n) => n.toLowerCase() !== result.name.toLowerCase()));
      return;
    }
    if (pinned.length >= MAX_PINS) {
      toast.error(`You can pin up to ${MAX_PINS} features`);
      return;
    }
    const next = [...pinned, result.name];
    onPinnedChange(next);
    if (next.length >= 2) {
      onModeChange("compare");
      toast.success(`Pinned "${result.name}"`, { description: "Showing the cheapest bundle that covers all pinned features." });
    } else {
      toast.success(`Pinned "${result.name}"`, { description: "Pin one or two more features to compare bundles." });
    }
  };

  const copyLink = async () => {
    const url = new URL(window.location.href);
    url.searchParams.set("lookup", feature);
    try {
      await navigator.clipboard.writeText(url.toString());
      toast.success("Lookup link copied");
    } catch {
      toast(url.toString());
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="lookup-sheet w-full sm:max-w-2xl p-0 flex flex-col"
        data-testid="lookup-sheet"
        onKeyDownCapture={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onOpenChange(false);
          }
        }}
      >
        <SheetHeader className="lookup-head">
          <SheetTitle className="lookup-title">
            <Crosshair size={18} aria-hidden="true" /> Which License?
          </SheetTitle>
          <SheetDescription>Type a feature and see every SKU and add-on path that grants it, cheapest first. Pin 2-3 features to find one bundle for all.</SheetDescription>
        </SheetHeader>

        <div className="lookup-body">
          {pinned.length > 0 && (
            <div className="pin-bar" data-testid="pin-bar">
              <span className="pin-label">
                <Pin size={13} aria-hidden="true" /> Pinned
              </span>
              {pinned.map((n) => (
                <span key={n} className={`pin-chip ${mode === "single" && feature?.toLowerCase() === n.toLowerCase() ? "active" : ""}`} data-testid="pin-chip">
                  <button type="button" onClick={() => selectFeature(n)} title="Open lookup for this feature">
                    {n}
                  </button>
                  <button type="button" className="pin-x" onClick={() => onPinnedChange(pinned.filter((x) => x !== n))} aria-label={`Unpin ${n}`} data-testid="pin-remove">
                    <X size={12} />
                  </button>
                </span>
              ))}
              <div className="spacer" />
              {mode === "compare" && pinned.length < MAX_PINS && (
                <button
                  type="button"
                  className="tb-btn sm"
                  onClick={() => {
                    onFeatureChange("");
                    onModeChange("single");
                  }}
                  data-testid="pin-add"
                >
                  <Plus size={13} /> Add feature
                </button>
              )}
              <button
                type="button"
                className={`tb-btn sm ${mode === "compare" ? "active" : ""}`}
                onClick={() => onModeChange(mode === "compare" ? "single" : "compare")}
                disabled={pinned.length < 2 && mode !== "compare"}
                data-testid="compare-toggle"
              >
                <Layers3 size={13} /> {mode === "compare" ? "Back to lookup" : `Compare ${pinned.length}`}
              </button>
              <button type="button" className="tb-btn sm ghost" onClick={() => onPinnedChange([])} data-testid="pin-clear">
                Clear
              </button>
            </div>
          )}

          {mode === "compare" ? (
            <ComparePanel
              dataset={dataset}
              plans={plans}
              addons={addons}
              groups={groups}
              priceMode={priceMode}
              pricing={pricing}
              pinned={pinned}
              onOpenFeature={selectFeature}
              cardRef={cardRef}
              onCardData={setCardData}
            />
          ) : (
            <>
              <Command className="lookup-cmd" loop>
                <CommandInput placeholder="Search a feature, e.g. Privileged Identity Management" value={query} onValueChange={setQuery} autoFocus data-testid="lookup-input" />
                {showList && (
                  <CommandList className="lookup-list" data-testid="lookup-list">
                    <CommandEmpty>No feature matches that name.</CommandEmpty>
                    <CommandGroup heading={`${names.length} features`}>
                      {names.map((n) => (
                        <CommandItem key={n.key} value={n.name} onSelect={() => selectFeature(n.name)} className="lookup-item" data-testid="lookup-option">
                          <span className="lookup-item-name">{n.name}</span>
                          <span className="lookup-item-tags">
                            {n.sections.map((s) => (
                              <span key={s} className={`mini-chip sec-${s}`}>
                                {SECTION_SHORT[s] || s}
                              </span>
                            ))}
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                )}
              </Command>

              {feature && !result && (
                <div className="lookup-empty" data-testid="lookup-no-result">
                  <Info size={16} /> "{feature}" is not in the current dataset.
                </div>
              )}

              {result && !showList && (
                <div className="lookup-results" data-testid="lookup-results">
                  <div className="lookup-feature">
                    <div>
                      <h3 data-testid="lookup-feature-name">{result.name}</h3>
                      <div className="lookup-contexts">
                        {result.contexts.map((c) => (
                          <span key={c.rowId} className="lookup-context">
                            <span className="mini-chip">{c.section}</span>
                            {c.parents.length > 0 && (
                              <span className="lookup-parents">
                                <CornerDownRight size={11} aria-hidden="true" /> {c.parents.join(" \u203a ")}
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="lookup-actions">
                      <button type="button" className={`tb-btn sm ${isPinned ? "active" : ""}`} onClick={togglePin} data-testid="lookup-pin" aria-pressed={isPinned}>
                        {isPinned ? <PinOff size={13} /> : <Pin size={13} />} {isPinned ? "Unpin" : "Pin to compare"}
                      </button>
                      {result.url && (
                        <a className="tb-btn sm" href={result.url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink size={13} /> Docs
                        </a>
                      )}
                      <button type="button" className="tb-btn sm" onClick={copyLink} data-testid="lookup-copy-link">
                        <Link2 size={13} /> Copy link
                      </button>
                      <ShareMenu cardRef={cardRef} title={result.name} />
                      <button type="button" className="tb-btn sm" onClick={() => onShowInMatrix(result.name)} data-testid="lookup-show-matrix">
                        <Table2 size={13} /> Show in matrix
                      </button>
                    </div>
                  </div>

                  <div className="lookup-summary">
                    <span className="chip">
                      <strong>{result.paths.length}</strong> paths
                    </span>
                    <span className="chip">
                      <strong>{result.directCount}</strong> included directly
                    </span>
                    {result.cheapest && (
                      <span className="chip cheapest" data-testid="lookup-cheapest">
                        <Sparkles size={12} aria-hidden="true" /> Cheapest: {result.cheapest.title} at {formatPrice(result.cheapest.total, symbol, result.cheapest.estimated)}
                      </span>
                    )}
                  </div>

                  {(usedGroups.length > 1 || hasStandalone) && (
                    <div className="pills lookup-groups" role="group" aria-label="Filter by license family">
                      <button type="button" className="pill" aria-pressed={groupFilter === "all"} onClick={() => setGroupFilter("all")}>
                        All
                      </button>
                      {usedGroups.map((g) => (
                        <button key={g.id} type="button" className="pill" aria-pressed={groupFilter === g.id} onClick={() => setGroupFilter(g.id)}>
                          {g.tableLabel}
                        </button>
                      ))}
                      {hasStandalone && (
                        <button type="button" className="pill" aria-pressed={groupFilter === "standalone"} onClick={() => setGroupFilter("standalone")}>
                          Standalone
                        </button>
                      )}
                    </div>
                  )}

                  <ol className="path-list">
                    {filteredPaths.map((p, idx) => (
                      <PathCard key={p.key} path={p} rank={idx + 1} isCheapest={result.cheapest && p.key === result.cheapest.key} groupLabel={groupLabel} symbol={symbol} />
                    ))}
                  </ol>
                  {filteredPaths.length === 0 && <div className="lookup-empty">No paths in this license family.</div>}
                  <p className="lookup-foot">
                    Prices: {pricing?.note}. {priceMode === "teams" ? "With Teams" : "No Teams"} suite pricing. {"\u2248"} marks prices converted from the US list
                    price. Add-on prices are indicative list prices - verify with Microsoft before purchasing.
                  </p>
                </div>
              )}

              {!feature && !query && (
                <div className="lookup-hint">
                  <Sparkles size={14} aria-hidden="true" /> Tip: press <kbd>Ctrl</kbd> + <kbd>K</kbd> anywhere to open this lookup, or hover a feature row and click the
                  crosshair.
                </div>
              )}
            </>
          )}
        </div>
        {open && cardData && <ShareCard cardRef={cardRef} {...cardData} pricing={pricing} priceMode={priceMode} />}
      </SheetContent>
    </Sheet>
  );
}
